import React from 'react';
import { Info, AlertTriangle, MessageCircle } from 'lucide-react';
import { useLang } from '../../context/LanguageContext';
import { Reveal, SectionHead } from '../motion';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { ActionLink, TICKMILL_REF_URL, COPY_URL, SUPPORT_URL, hideReferral } from './links';

export function Know() {
  const { t } = useLang();
  const items = t('dcapital.knowItems');
  return (
    <section id="know" className="py-24 sm:py-32" data-testid="dc-know">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead overline={t('dcapital.knowOverline')} title={t('dcapital.knowTitle')} />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-9">
          {items.map((item, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08}>
              <div data-testid={`dc-know-${i}`} className="flex gap-4">
                <span className="mt-1 flex w-8 h-8 shrink-0 items-center justify-center rounded-md border border-blue-500/30 bg-blue-500/10 font-display text-sm text-blue-300">
                  {i + 1}
                </span>
                <div>
                  <h3 className="font-display text-base text-white">{item.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{item.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Faq() {
  const { t } = useLang();
  const faqs = hideReferral(t('dcapital.faqs'));
  return (
    <section id="faq" className="py-24 sm:py-32 bg-[#111D36] border-y border-slate-800" data-testid="dc-faq">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <SectionHead overline={t('dcapital.faqOverline')} title={t('dcapital.faqTitle')} align="center" />
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-12 space-y-3" data-testid="dc-faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="rounded-xl border border-slate-700/70 bg-[#1E293B] px-6 data-[state=open]:border-blue-500/50">
                <AccordionTrigger data-testid={`dc-faq-trigger-${i}`} className="text-left font-display text-base sm:text-lg text-slate-100 hover:text-blue-300 hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-slate-400 leading-relaxed pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}

export function Risk() {
  const { t } = useLang();
  const paras = t('dcapital.riskParas');
  return (
    <section id="risk" className="py-24 sm:py-28" data-testid="dc-risk">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <Reveal>
          <div className="rounded-xl border border-rose-500/40 bg-rose-500/5 p-8 sm:p-10">
            <p className="text-xs font-semibold tracking-widest text-rose-400 flex items-center gap-2 uppercase">
              <AlertTriangle className="w-4 h-4" /> {t('dcapital.riskOverline')}
            </p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-white">{t('dcapital.riskTitle')}</h2>
            <div className="mt-6 space-y-4">
              {paras.map((p, i) => (
                <p key={i} data-testid={`dc-risk-para-${i}`} className={`text-sm leading-relaxed ${i === paras.length - 1 ? 'text-rose-200 font-semibold' : 'text-slate-300'}`}>
                  {p}
                </p>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function FinalCta() {
  const { t } = useLang();
  return (
    <section className="py-24 sm:py-28 bg-gradient-to-r from-green-700 via-green-600 to-emerald-700 relative overflow-hidden" data-testid="dc-final-cta">
      <div className="relative max-w-3xl mx-auto px-4 text-center">
        <Reveal>
          <h2 className="font-display text-4xl sm:text-5xl text-white leading-[1.05]">{t('dcapital.finalTitle')}</h2>
          <p className="mt-5 text-green-100/90 max-w-xl mx-auto">{t('dcapital.finalSub')}</p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <ActionLink href={TICKMILL_REF_URL} label={t('dcapital.ctaRegister')} testId="dc-final-register-btn" variant="light" big />
            <ActionLink href={COPY_URL} label={t('dcapital.ctaCopy')} testId="dc-final-copy-btn" big />
          </div>
          <p className="mt-10 text-sm text-green-100/80 flex items-center justify-center gap-2 flex-wrap">
            <Info className="w-4 h-4" /> {t('dcapital.helpText')}
            <a data-testid="dc-help-link" href={SUPPORT_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-semibold text-white underline underline-offset-4 hover:text-green-100">
              <MessageCircle className="w-4 h-4" /> {t('dcapital.helpBtn')}
            </a>
          </p>
        </Reveal>
      </div>
    </section>
  );
}
